import type { Deck } from "../schema";
import { compileSteps, entryStep } from "./compile";

/** What navigation needs to know about one page: its id and its click steps. */
export interface PageSteps {
  id: string;
  /** number of steps on the page (0 when it has no animations) */
  count: number;
  /** step shown on arrival: 0 when the first step plays by itself, else -1 (nothing played yet) */
  entry: number;
  /** hidden pages are skipped by next/prev/first/last (goto still reaches them) */
  hidden?: boolean;
}

export interface DeckPosition {
  page: number;
  /** -1 = page shown, no step played yet */
  step: number;
}

export type NavAction =
  | { action: "next" }
  | { action: "prev" }
  | { action: "first" }
  | { action: "last" }
  /** go to a page by index or id; `step` defaults to the page's entry step */
  | { action: "goto"; page?: number; pageId?: string; step?: number };

/** Step summary for every page, in order. Shared by the presenter's browser and the room. */
export function deckSteps(deck: Pick<Deck, "pages">): PageSteps[] {
  return deck.pages.map((p) => {
    const steps = compileSteps(p.timeline);
    return { id: p.id, count: steps.length, entry: entryStep(steps), ...(p.hidden ? { hidden: true } : {}) };
  });
}

/**
 * Where an action moves the presentation. next/prev walk the click steps and cross page
 * boundaries (prev lands on the last step of the page before); first/last and goto land on the
 * entry step. Returns null when the action names a page that does not exist.
 */
export function navigate(pages: PageSteps[], pos: DeckPosition, nav: NavAction): DeckPosition | null {
  if (pages.length === 0) return null;
  const last = pages.length - 1;
  const at = (page: number): DeckPosition => ({ page, step: pages[page]!.entry });
  const cur = Math.min(Math.max(pos.page, 0), last);
  /** next shown page from `from` going `dir`, or -1 */
  const shown = (from: number, dir: 1 | -1): number => {
    for (let i = from; i >= 0 && i <= last; i += dir) if (!pages[i]!.hidden) return i;
    return -1;
  };
  switch (nav.action) {
    case "next": {
      if (pos.step < pages[cur]!.count - 1) return { page: cur, step: pos.step + 1 };
      const to = shown(cur + 1, 1);
      return to >= 0 ? at(to) : { page: cur, step: pos.step };
    }
    case "prev": {
      if (pos.step > pages[cur]!.entry) return { page: cur, step: pos.step - 1 };
      const to = shown(cur - 1, -1);
      return to >= 0 ? { page: to, step: pages[to]!.count - 1 } : { page: cur, step: pos.step };
    }
    case "first":
      return at(Math.max(0, shown(0, 1)));
    case "last":
      return at(shown(last, -1) >= 0 ? shown(last, -1) : last);
    case "goto": {
      const page = nav.pageId !== undefined ? pages.findIndex((p) => p.id === nav.pageId) : (nav.page ?? -1);
      if (!Number.isInteger(page) || page < 0 || page > last) return null;
      if (nav.step === undefined) return at(page);
      return { page, step: Math.min(Math.max(nav.step, -1), pages[page]!.count - 1) };
    }
  }
}
